import { ReactElement } from "react";
import { ITreeMapChartConfig, ITreeMapNode } from "./types";
import Chart from "./chart";
import { useChartDimensions, useUniqueId } from "./chartUtils";
import * as d3 from "d3";
import Legend from "./legend";
import HtmlTooltip from "./tooltip";
import { formatNumber } from "../../system/utils";

interface ILineChartProps {
  chartConfig: ITreeMapChartConfig;
}

const compare = (a: d3.HierarchyNode<ITreeMapNode>, b: d3.HierarchyNode<ITreeMapNode>): number => (b.value ?? 0) - (a.value ?? 0);

const TreeMapChart = ({ chartConfig }: ILineChartProps): ReactElement => {
  const [ref, dimensions] = useChartDimensions<HTMLDivElement>({
    height: chartConfig.height ?? undefined,
    width: chartConfig.width ?? undefined,
    marginBottom: 10,
    marginLeft: 10,
    marginRight: 10,
    marginTop: chartConfig.showLegend  ? 20 : 10
  });

  const chartID = useUniqueId("treemap-chart");

  const hierarchy = d3.hierarchy<ITreeMapNode>(chartConfig.root)
    .sum(d => d.value)  // sum every child's values
    .sort(compare);

  const treemap = d3.treemap<ITreeMapNode>()
    .size([dimensions.boundedWidth, dimensions.boundedHeight])
    .padding(1);

  const root = treemap(hierarchy);

  return (
    <div ref={ref} >
      <Chart dimensions={dimensions} >
        { chartConfig.showLegend && <Legend chartID={chartID} series={chartConfig.series} width={dimensions.boundedWidth} /> }
        { root.leaves().map((item, index) => {
          const node = item.data as ITreeMapNode;

          return(
            <HtmlTooltip key={`${chartID}-rect-${index}`} title={
              <div className="display-flex-column">
                <div className="display-flex-column align-items-center" >
                  <strong>{node.name}</strong>
                </div>
                <div className="display-flex-row align-items-center" >
                  <div style={{ background: node.color, width: 10, height: 10, marginRight: 10 }} />
                  <strong className="flex-grow-1">{item.parent ? (item.parent.data as ITreeMapNode).name : ""}</strong>
                  <strong className="flex-grow-0">
                    {formatNumber((item.data as ITreeMapNode).value, 1)}
                  </strong>
                </div>
              </div>
            }>
              <g>
                <rect
                  className="Bars__rect"
                  fill={node.color}
                  height={Math.max(0, item.y1 - item.y0)}
                  width={Math.max(0, item.x1 - item.x0)}
                  y={item.y0}
                  x={item.x0}
                />
                <text
                  y={item.y0 + 7}
                  x={item.x0 + 2}
                  alignmentBaseline="middle"
                  fontSize=".7em"
                  fill={"whitesmoke"}>
                  {node.name}
                </text>
              </g>
            </HtmlTooltip>
          );
        })
        }
      </Chart>
    </div>);
};

export default TreeMapChart;
